import React, { useState, useEffect } from "react";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";

const getCountdown = (expirationDate) => {
  if (!expirationDate) return null;
  const diff = new Date(expirationDate) - new Date();
  if (diff <= 0) return null;
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  if (hours > 48) return `${Math.ceil(hours / 24)} días`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

const UpdateCard = ({ update, onDownload, onRequestRenewal }) => {
  const [countdown, setCountdown] = useState(() => getCountdown(update?.expirationDate));

  useEffect(() => {
    if (!update?.expirationDate || update?.isExpired) return;
    const interval = setInterval(() => {
      setCountdown(getCountdown(update?.expirationDate));
    }, 60000);
    return () => clearInterval(interval);
  }, [update?.expirationDate, update?.isExpired]);

  const isLimitReached = update?.downloadLimit > 0 && update?.downloadCount >= update?.downloadLimit;
  const isExpired = update?.isExpired;
  const isAvailable = !isExpired && !isLimitReached;

  const statusConfig = {
    disponible: { label: "Disponible", bg: "bg-emerald-100", text: "text-emerald-700", icon: "CheckCircle" },
    descargado: { label: "Descargado", bg: "bg-blue-100", text: "text-blue-700", icon: "Download" },
    expirado: { label: "Vencido", bg: "bg-red-100", text: "text-red-700", icon: "XCircle" },
  };

  const effectiveStatus = isExpired ? 'expirado' : isLimitReached ? 'expirado' : update?.status;
  const sc = statusConfig?.[effectiveStatus] || statusConfig?.disponible;

  return (
    <div
      className="rounded-xl border overflow-hidden transition-all hover:shadow-md"
      style={{
        backgroundColor: "var(--color-card)",
        borderColor: isExpired ? "rgba(239,68,68,0.3)" : "var(--color-border)",
        boxShadow: "var(--shadow-sm)",
      }}
    >
      {/* Header */}
      <div className="px-4 pt-4 pb-3 border-b" style={{ borderColor: "var(--color-border)" }}>
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: "rgba(15,118,110,0.1)" }}>
            <Icon name="Package" size={20} color="var(--color-primary)" />
          </div>
          <span className={`font-caption text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${sc?.bg} ${sc?.text}`}>{sc?.label}</span>
        </div>
        <h3 className="font-heading font-bold text-base leading-tight" style={{ color: "var(--color-foreground)" }}>{update?.name}</h3>
        <p className="font-caption text-xs mt-0.5" style={{ color: "var(--color-muted-foreground)" }}>Versión {update?.version}</p>
      </div>

      {/* Body */}
      <div className="px-4 py-3 space-y-2">
        {update?.description && (
          <p className="font-caption text-xs line-clamp-2" style={{ color: "var(--color-muted-foreground)" }}>{update?.description}</p>
        )}

        <div className="grid grid-cols-2 gap-2 text-xs font-caption">
          {update?.fileSize && update?.fileSize !== '—' && (
            <div className="flex items-center gap-1" style={{ color: "var(--color-muted-foreground)" }}>
              <Icon name="HardDrive" size={12} color="currentColor" />
              <span>{update?.fileSize}</span>
            </div>
          )}
          <div className="flex items-center gap-1" style={{ color: "var(--color-muted-foreground)" }}>
            <Icon name="Calendar" size={12} color="currentColor" />
            <span>{update?.assignedDate}</span>
          </div>
        </div>

        {/* Expiration countdown */}
        {!isExpired && update?.expirationDate && (
          <div
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-caption"
            style={{
              backgroundColor: (update?.daysLeft !== null && update?.daysLeft <= 1) ? "rgba(239,68,68,0.08)" : "rgba(15,118,110,0.08)",
              color: (update?.daysLeft !== null && update?.daysLeft <= 1) ? "var(--color-error)" : "var(--color-primary)",
            }}
          >
            <Icon name="Clock" size={12} color="currentColor" />
            <span>Vence en: <strong>{countdown || `${update?.daysLeft} días`}</strong></span>
          </div>
        )}

        {/* Expired notice */}
        {isExpired && (
          <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-caption" style={{ backgroundColor: "rgba(239,68,68,0.08)", color: "var(--color-error)" }}>
            <Icon name="AlertCircle" size={12} color="currentColor" />
            <span>Enlace vencido el {update?.expirationDate ? new Date(update?.expirationDate)?.toLocaleDateString('es-CO') : '—'}</span>
          </div>
        )}

        {/* Download limit */}
        {update?.downloadLimit > 0 && (
          <div className="space-y-1">
            <div className="flex justify-between text-xs font-caption" style={{ color: "var(--color-muted-foreground)" }}>
              <span>Descargas</span>
              <span className={isLimitReached ? 'text-orange-600 font-semibold' : ''}>{update?.downloadCount}/{update?.downloadLimit}</span>
            </div>
            <div className="w-full h-1.5 rounded-full" style={{ backgroundColor: "var(--color-muted)" }}>
              <div
                className="h-1.5 rounded-full transition-all"
                style={{
                  width: `${Math.min((update?.downloadCount / update?.downloadLimit) * 100, 100)}%`,
                  background: isLimitReached ? "var(--color-error)" : "var(--color-primary)",
                }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 pb-4">
        {isAvailable ? (
          <Button
            variant="default"
            fullWidth
            iconName="Download"
            iconPosition="left"
            onClick={() => onDownload?.(update)}
          >
            Descargar
          </Button>
        ) : isLimitReached ? (
          <div className="text-center py-2">
            <p className="font-caption text-xs font-semibold" style={{ color: "#ea580c" }}>Límite de descargas alcanzado</p>
          </div>
        ) : (
          <Button
            variant="outline"
            fullWidth
            iconName="RefreshCw"
            iconPosition="left"
            onClick={() => onRequestRenewal?.(update)}
            className="border-red-200 text-red-600 hover:bg-red-50"
          >
            Solicitar renovación
          </Button>
        )}
      </div>
    </div>
  );
};

export default UpdateCard;